import type { GccDashboardData, GccDispositionTag, GccLocation, GccOwnership, GccWave } from '@shared/types'
import { splitExcludedLedger } from './s0bExclusions'

/**
 * One task of the export's Work View (WI 2882): a value ledger line with only what the
 * view draws. No pay rates, costs, tiers, segment or rationale, so the Simple (client)
 * export can embed it as it is.
 */
interface GccWorkViewTask {
  functionScope: string
  capabilityName: string
  roleTitle: string
  taskName: string
  baselineFte: number
  aiReleasedFte: number
  residualFte: number
  disposition: GccDispositionTag
  ownership: GccOwnership
  location: GccLocation
  transformWave: GccWave | null
  migrationWave: GccWave | null
}

/** FTE totals of the Work View, summed in cents so they match the value ledger's. */
interface GccWorkViewTotals {
  functions: number
  capabilities: number
  tasks: number
  currentFte: number
  aiReleasedFte: number
  residualFte: number
  /** Residual FTE per end-state owner */
  futureFteByOwnership: Record<GccOwnership, number>
}

export interface GccWorkView {
  tasks: GccWorkViewTask[]
  totals: GccWorkViewTotals
  /** Tasks the S0b review excluded: left out of the view, still in the design's figures */
  excluded: { tasks: number; fte: number }
}

const cents = (n: number) => Math.round((Number(n) || 0) * 100)
const sumFte = <T>(items: T[], pick: (x: T) => number) => items.reduce((s, x) => s + cents(pick(x)), 0) / 100

/**
 * The Work View of a design (WI 2882): every value ledger line the S0b review kept, in
 * ledger (register) order, with its totals. Null for a design saved before the value
 * ledger (WI 2326), which the view replaces with a note.
 *
 * S0b exclusions are display-only, so Art of the Possible still counts excluded tasks.
 * With nothing excluded these totals equal the ledger's; otherwise `excluded` says
 * how much was left out.
 */
export function gccWorkView(gcc: Pick<GccDashboardData, 'valueCase' | 's0bReview'>): GccWorkView | null {
  if (!gcc.valueCase) return null
  const { kept, excluded } = splitExcludedLedger(gcc)
  const tasks: GccWorkViewTask[] = kept.map(l => ({
    functionScope: l.functionScope,
    capabilityName: l.capabilityName,
    roleTitle: l.roleTitle,
    taskName: l.taskName,
    baselineFte: l.baselineFte,
    aiReleasedFte: l.aiReleasedFte,
    residualFte: l.residualFte,
    disposition: l.disposition,
    ownership: l.ownership,
    location: l.location,
    transformWave: l.transformWave,
    migrationWave: l.migrationWave,
  }))
  const owned = (o: GccOwnership) => sumFte(tasks.filter(t => t.ownership === o), t => t.residualFte)
  return {
    tasks,
    totals: {
      functions: new Set(tasks.map(t => t.functionScope)).size,
      capabilities: new Set(tasks.map(t => JSON.stringify([t.functionScope, t.capabilityName]))).size,
      tasks: tasks.length,
      currentFte: sumFte(tasks, t => t.baselineFte),
      aiReleasedFte: sumFte(tasks, t => t.aiReleasedFte),
      residualFte: sumFte(tasks, t => t.residualFte),
      futureFteByOwnership: { 'Captive-GCC': owned('Captive-GCC'), MSP: owned('MSP'), Retained: owned('Retained') },
    },
    excluded: { tasks: excluded.length, fte: sumFte(excluded, l => l.baselineFte) },
  }
}
